import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Disclosure } from 'shared/constants';
import * as Consent from 'shared/constants/consent';
import {
  usePersonalLoan,
  useDisclosure,
  useLoader,
  useProfile,
  usePersonalLoanRouteFsm,
  useTimeout,
  useTelemetry,
} from 'shared/hooks';
import { AdditionalProfileV2 } from './AdditionalProfileV2';

let watchId;

const AdditionalProfileContainer = () => {
  const { uuid: personalLoanUuid } = useParams();
  const history = useHistory();
  const {
    personalLoan,
    getPersonalLoanConsentTypes,
    updatePersonalLoan,
    error: personalLoanError,
    loading: personalLoanLoading,
  } = usePersonalLoan();
  const { saveSsn, profile } = useProfile();
  const {
    disclosuresList,
    downloadDocument,
    esignDisclosure,
    loading: disclosureLoading,
  } = useDisclosure(personalLoanUuid, personalLoan?.status?.disclosurelistUuid);
  const runRouter = usePersonalLoanRouteFsm();
  const { telemetry } = useTelemetry();
  const [documentUrl, setDocumentUrl] = useState('');
  const [geolocation, setGeolocation] = useState({
    latitude: 0,
    longitude: 0,
  });
  const unsignedDisclosures = disclosuresList.filter(
    (d) => d.type === Disclosure.APPLICATION_DISCLOSURE && d.signatureRequired && !d.hasEsignature
  );

  useLoader('Generating important disclosures', !personalLoan.status?.disclosurelistAppReady);

  useTimeout(() => {
    if (!personalLoan.status?.disclosurelistAppReady)
      history.replace(`/applications/${personalLoanUuid}/review-app/timeout`);
  }, 20000);

  const onDownloadDocument = async (disclosure) => {
    const data = await downloadDocument(
      personalLoanUuid,
      personalLoan.status.disclosurelistUuid,
      disclosure.document.uuid
    );
    const blob = new Blob([data], { type: 'application/pdf' });
    setDocumentUrl(window.URL.createObjectURL(blob));
  };

  const handleFormSubmit = async (data) => {
    const consentTypes = await getPersonalLoanConsentTypes();
    const creditType = consentTypes.find((c) => c.type === Consent.HARD_CREDIT);

    await Promise.all(
      data.documents.map((c) =>
        esignDisclosure({
          applicationUuid: personalLoanUuid,
          documentUuid: c,
          latitude: geolocation.latitude,
          longitude: geolocation.longitude,
          consentToEsign: true,
          antiCoercionChoice: data.antiCoercionChoice,
        })
      )
    );
    
    // only saveSSN if there is one
    data?.ssn?.length > 0 && (await saveSsn({ ssn: data.ssn.replace(/-/g,'') }));
    await updatePersonalLoan(personalLoanUuid, {
      ...personalLoan,
      consentsList: [
        ...personalLoan.consentsList,
        {
          type: creditType.type,
          version: creditType.version,
          optInOut: 'OPT_IN',
        },
      ],
    });
    telemetry('plMoreInfo');
    runRouter();
  };

  useEffect(() => {
    if (
      unsignedDisclosures?.length > 0 &&
      !watchId &&
      window &&
      'geolocation' in window.navigator
    ) {
      watchId = window.navigator.geolocation.watchPosition((p) => {
        setGeolocation({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
        });
      });
    }
  }, [unsignedDisclosures]);

  if (personalLoanLoading || disclosureLoading) {
    return null;
  }

  return (
    <AdditionalProfileV2
      documentUrl={documentUrl}
      handleSubmit={handleFormSubmit}
      handleDownload={onDownloadDocument}
      personalLoan={personalLoan}
      profile={profile}
      disclosures={unsignedDisclosures}
      serverError={personalLoanError}
    />
  );
};

export default AdditionalProfileContainer;
